<template>
  <div class="register-by-invitation">
    <h2>{{ t('registration.invitationCode.title', 'Register using an invitation code') }}</h2>
    <p class="description">
      {{ t('registration.invitationCode.description', 'If you have received an invitation code, enter it below to proceed with registration.') }}
    </p>
    <form
      @submit.prevent="handleSubmit"
      class="invitation-form"
    >
      <div class="form-group">
        <label for="invitation-code">{{ t('registration.invitationCode.codeLabel', 'Invitation code') }} *</label>
        <input
          id="invitation-code"
          v-model="code"
          type="text"
          required
          :disabled="isSubmitting"
          :placeholder="t('registration.invitationCode.codePlaceholder', 'Enter your invitation code')"
          :class="{ 'input-error': errorMessage }"
        >
        <div
          v-if="errorMessage"
          class="error-message"
          v-html="errorMessage.replace(/\n/g, '<br/>')"
        />
      </div>
      <div class="form-actions">
        <button
          type="submit"
          class="btn-primary"
          :disabled="isSubmitting || !code?.trim()"
        >
          {{ isSubmitting ? t('registration.invitationCode.validating', 'Validating...') : t('registration.invitationCode.submit', 'Continue to registration') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { r7eBaseApp } from '../services/index.js';

const { t } = useI18n();

const product = computed(() => r7eBaseApp.account_type || 'publisher');

const code = ref('');
const isSubmitting = ref(false);
const errorMessage = ref('');

const emit = defineEmits(['code-validated']);

async function handleSubmit() {
  errorMessage.value = '';
  const trimmedCode = code.value?.trim();
  if (!trimmedCode) return;

  isSubmitting.value = true;
  try {
    const result = await r7eBaseApp.checkInvitationCode(trimmedCode, product.value);
    console.log("result", result);
    if (result?.valid) {
      emit('code-validated', trimmedCode);
    } else {
      errorMessage.value = '';
      if (result?.disabled) {
        errorMessage.value = errorMessage.value + '\n' + t('registration.invitationCode.disabledCode', 'This invitation code is disabled.');
      } else {
        if (result?.expired) {
          errorMessage.value = errorMessage.value + '\n' + t('registration.invitationCode.expiredCode', 'This invitation code has expired.');
        } 
        if (result?.limit_reached) {
          errorMessage.value = errorMessage.value + '\n' + t('registration.invitationCode.limitReachedCode', 'This invitation code has reached the maximum number of uses.');
        } 
        if (result?.unexpected_product) {
          errorMessage.value = errorMessage.value + '\n' + t('registration.invitationCode.unexpectedProductCode', 'This invitation code is for a different product.');
        }
        if (result?.not_found) {
          errorMessage.value = errorMessage.value + '\n' + t('registration.invitationCode.notFoundCode', 'This invitation code is not found.');
        }    
      }
      if (!errorMessage.value) {
        errorMessage.value = errorMessage.value + '\n' + t('registration.invitationCode.invalidCode', 'This invitation code is not valid.');
      }
    }
  } catch (err) {
    const msg = err.response?.data?.message || err.message || t('registration.invitationCode.error', 'Failed to validate the code. Please try again.');
    errorMessage.value = msg;
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.register-by-invitation {
  max-width: 400px;
  margin: 0 auto;
  padding: 1.5rem;
}

.register-by-invitation h2 {
  color: var(--color-text-dark);
  margin-bottom: 0.75rem;
  font-size: 1.5em;
}

.description {
  color: var(--color-text);
  font-size: 1em;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.invitation-form .form-group {
  margin-bottom: 1rem;
}

.invitation-form label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-text-dark);
  font-weight: 500;
}

.invitation-form input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
}

.invitation-form input.input-error {
  border-color: var(--color-error, #dc3545);
}

.invitation-form input:disabled {
  background-color: var(--color-frame-bg);
  cursor: not-allowed;
}

.error-message {
  color: var(--color-error, #dc3545);
  font-size: 0.9em;
  margin-top: 0.5rem;
}

.form-actions {
  margin-top: 1.25rem;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background-color: var(--color-safe);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
